import { useEffect, useState } from "react"
import AdminSidebar from "../../components/admin/AdminSidebar"
import AdminHeader from "../../components/admin/AdminHeader"
import { Button } from "../../components/ui/button"
import { Badge } from "../../components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar"
import ImageUpload from "../../components/ImageUpload"
import { api } from "../../utils/apiComponent/apiClient"
import { endpoints } from "../../utils/endpoints"
import { showErrorToast, showSuccessToast } from "../../utils/toast"
import {
  Edit,
  Eye,
  EyeOff,
  Loader2,
  Pin,
  Plus,
  Star,
  Trash2,
  X
} from "lucide-react"

const emptyForm = {
  authorName: "",
  authorRole: "",
  authorAvatar: "",
  authorCompany: "",
  content: "",
  rating: 5,
  isFeatured: false,
  isPublished: false,
  order: 0
}

// Crée un FormData pour l'upload d'avatar de témoignage
function createTestimonialFormData(testimonialData) {
  const formData = new FormData()

  if (testimonialData.authorName) formData.append('authorName', testimonialData.authorName)
  if (testimonialData.authorRole) formData.append('authorRole', testimonialData.authorRole)
  if (testimonialData.authorCompany) formData.append('authorCompany', testimonialData.authorCompany)
  if (testimonialData.content) formData.append('content', testimonialData.content)
  if (testimonialData.rating !== undefined) formData.append('rating', testimonialData.rating.toString())
  if (testimonialData.order !== undefined) formData.append('order', testimonialData.order.toString())
  if (testimonialData.isFeatured !== undefined) formData.append('isFeatured', testimonialData.isFeatured.toString())
  if (testimonialData.isPublished !== undefined) formData.append('isPublished', testimonialData.isPublished.toString())

  // Fichier avatar
  if (testimonialData.avatarFile instanceof File || testimonialData.avatarFile instanceof Blob) {
    formData.append('authorAvatar', testimonialData.avatarFile)
  } else if (testimonialData.authorAvatar) {
    formData.append('authorAvatar', testimonialData.authorAvatar)
  }

  return formData
}

export default function TestimonialsManagementPage() {
  const [testimonials, setTestimonials] = useState([])
  const [loading, setLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTestimonial, setEditingTestimonial] = useState(null)
  const [formData, setFormData] = useState(emptyForm)
  const [avatarFile, setAvatarFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [filterPublished, setFilterPublished] = useState("all")
  const [filterFeatured, setFilterFeatured] = useState("all")

  const loadTestimonials = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: "1", limit: "100" })
      if (filterPublished !== "all") params.append("isPublished", filterPublished)
      if (filterFeatured !== "all") params.append("isFeatured", filterFeatured)

      const response = await api.get(`${endpoints.testimonials}?${params.toString()}`, { isPublicRoute: true })
      const data = response?.data || response
      const list = Array.isArray(data)
        ? data
        : data?.testimonials || data?.items || data?.data || []

      setTestimonials(Array.isArray(list) ? list : [])
    } catch (error) {
      showErrorToast({ message: "Erreur lors du chargement des témoignages" })
      setTestimonials([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTestimonials()
  }, [filterPublished, filterFeatured])

  const openCreateModal = () => {
    setEditingTestimonial(null)
    setFormData({ ...emptyForm, order: testimonials.length })
    setAvatarFile(null)
    setIsModalOpen(true)
  }

  const openEditModal = (testimonial) => {
    setEditingTestimonial(testimonial)
    setFormData({
      authorName: testimonial.authorName || "",
      authorRole: testimonial.authorRole || "",
      authorAvatar: testimonial.authorAvatar || "",
      authorCompany: testimonial.authorCompany || "",
      content: testimonial.content || "",
      rating: testimonial.rating || 5,
      isFeatured: !!testimonial.isFeatured,
      isPublished: !!testimonial.isPublished,
      order: testimonial.order || 0
    })
    setAvatarFile(null)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingTestimonial(null)
    setFormData(emptyForm)
    setAvatarFile(null)
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = {
        ...formData,
        avatarFile,
        rating: parseInt(formData.rating, 10) || 5,
        order: parseInt(formData.order, 10) || 0
      }

      // Utiliser FormData si on a un fichier avatar
      const requestData = (avatarFile instanceof File || avatarFile instanceof Blob)
        ? createTestimonialFormData(payload)
        : payload

      if (editingTestimonial) {
        await api.patch(endpoints.updateTestimonial(editingTestimonial.id), requestData)
        showSuccessToast({ message: "Témoignage modifié avec succès" })
      } else {
        await api.post(endpoints.createTestimonial, requestData)
        showSuccessToast({ message: "Témoignage créé avec succès" })
      }

      closeModal()
      await loadTestimonials()
    } catch (error) {
      showErrorToast({ message: error?.message || "Erreur lors de l'enregistrement" })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (testimonial) => {
    if (!window.confirm(`Supprimer le témoignage de ${testimonial.authorName} ?`)) return

    try {
      await api.delete(endpoints.deleteTestimonial(testimonial.id))
      showSuccessToast({ message: "Témoignage supprimé avec succès" })
      await loadTestimonials()
    } catch (error) {
      showErrorToast({ message: error?.message || "Erreur lors de la suppression" })
    }
  }

  const handleToggleFeatured = async (testimonial) => {
    try {
      await api.post(endpoints.toggleTestimonialFeatured(testimonial.id))
      showSuccessToast({ message: "Mise en avant mise à jour" })
      await loadTestimonials()
    } catch (error) {
      showErrorToast({ message: "Erreur lors de la mise en avant" })
    }
  }

  const handleTogglePublished = async (testimonial) => {
    try {
      await api.post(endpoints.toggleTestimonialPublished(testimonial.id))
      showSuccessToast({ message: "Publication mise à jour" })
      await loadTestimonials()
    } catch (error) {
      showErrorToast({ message: "Erreur lors de la publication" })
    }
  }

  const handleReorderByCurrentOrder = async () => {
    try {
      const testimonialIds = [...testimonials]
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(testimonial => testimonial.id)
      await api.post(endpoints.reorderTestimonials, { testimonialIds })
      showSuccessToast({ message: "Témoignages réorganisés avec succès" })
      await loadTestimonials()
    } catch (error) {
      showErrorToast({ message: "Erreur lors de la réorganisation" })
    }
  }

  return (
    <div className="flex min-h-screen bg-gray-100 overflow-x-hidden">
      <AdminSidebar />
      <main className="flex-1 p-8 ml-64 max-w-full overflow-x-hidden">
        <AdminHeader
          title="Gestion des témoignages"
          subtitle="Créer, publier et mettre en avant les témoignages clients"
          showCreateButton={true}
          createButtonText="Créer un témoignage"
          onCreateClick={openCreateModal}
        />

        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <select
              value={filterPublished}
              onChange={(e) => setFilterPublished(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
            >
              <option value="all">Toutes les publications</option>
              <option value="true">Publiés</option>
              <option value="false">Non publiés</option>
            </select>
            <select
              value={filterFeatured}
              onChange={(e) => setFilterFeatured(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
            >
              <option value="all">Toutes les mises en avant</option>
              <option value="true">Mis en avant</option>
              <option value="false">Non mis en avant</option>
            </select>
            <Button variant="outline" onClick={handleReorderByCurrentOrder}>
              Réorganiser selon l'ordre
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#141835]" />
            </div>
          ) : testimonials.length === 0 ? (
            <div className="text-center py-12">
              <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucun témoignage trouvé</p>
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="min-w-[1150px] divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auteur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contenu</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Note</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statuts</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ordre</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 shadow-[-8px_0_12px_-12px_rgba(0,0,0,0.35)]">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {testimonials.map((testimonial) => (
                    <tr key={testimonial.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={testimonial.authorAvatar} />
                            <AvatarFallback>{testimonial.authorName?.charAt(0)?.toUpperCase() || "T"}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{testimonial.authorName}</div>
                            <div className="text-xs text-gray-500">{testimonial.authorRole}</div>
                            {testimonial.authorCompany && <div className="text-xs text-gray-400">{testimonial.authorCompany}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-700 max-w-md line-clamp-2">{testimonial.content}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 text-yellow-500">
                          {[...Array(5)].map((_, index) => (
                            <Star key={index} className={`w-4 h-4 ${index < (testimonial.rating || 0) ? "fill-current" : "text-gray-300"}`} />
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-2">
                          <Badge className={testimonial.isPublished ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                            {testimonial.isPublished ? "Publié" : "Non publié"}
                          </Badge>
                          <Badge className={testimonial.isFeatured ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-800"}>
                            {testimonial.isFeatured ? "Mis en avant" : "Standard"}
                          </Badge>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{testimonial.order ?? 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium sticky right-0 bg-white shadow-[-8px_0_12px_-12px_rgba(0,0,0,0.35)]">
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(testimonial)} title="Modifier">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleTogglePublished(testimonial)} title="Publier/Dépublier">
                            {testimonial.isPublished ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleToggleFeatured(testimonial)} title="Mettre en avant">
                            <Pin className={`w-4 h-4 ${testimonial.isFeatured ? "fill-current text-yellow-600" : ""}`} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(testimonial)} className="text-red-600 hover:text-red-700" title="Supprimer">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                {editingTestimonial ? "Modifier le témoignage" : "Créer un témoignage"}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'auteur *</label>
                  <input name="authorName" value={formData.authorName} onChange={handleChange} required className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                  <input name="authorRole" value={formData.authorRole} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise</label>
                  <input name="authorCompany" value={formData.authorCompany} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Avatar</label>
                  <ImageUpload
                    onImageSelect={(file) => setAvatarFile(file)}
                    currentImage={formData.authorAvatar}
                    placeholder="Cliquez pour sélectionner une image d'avatar"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                  <input name="rating" type="number" min="1" max="5" value={formData.rating} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ordre</label>
                  <input name="order" type="number" min="0" value={formData.order} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contenu *</label>
                <textarea name="content" value={formData.content} onChange={handleChange} required rows={5} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]" />
              </div>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" name="isPublished" checked={formData.isPublished} onChange={handleChange} />
                  Publié
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" name="isFeatured" checked={formData.isFeatured} onChange={handleChange} />
                  Mis en avant
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={closeModal} disabled={saving}>Annuler</Button>
                <Button type="submit" disabled={saving} className="bg-[#141835] hover:bg-[#1a1f4a] text-white">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingTestimonial ? "Modifier" : "Créer"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
